// Shared helpers for the URL-first brand flow. The /brands create form
// and the onboarding URL step both treat a raw input as a marketing-site
// URL: bare domains ("bike.uy") are accepted (we prepend https:// before
// parsing), and the create form derives a starter brand name from the
// domain's main label.

/** True when `raw` parses as a host-bearing URL with at least one dot in
 *  the hostname (so `localhost` / `intranet` don't pass — the scraper
 *  wants a public marketing site). Accepts bare domains. */
export function isValidSiteUrl(raw: string): boolean {
  const v = raw.trim();
  if (!v) return false;
  try {
    const u = new URL(/^https?:\/\//i.test(v) ? v : `https://${v}`);
    return u.hostname.includes('.');
  } catch {
    return false;
  }
}

/** Derive a starter brand name from a site URL — the domain's main
 *  label, lowercased ("www.commitmind.dev" → "commitmind"). It's only a
 *  placeholder: the founder reviews mag's extracted company name in the
 *  brief and can rename anytime. Returns null when no host is parseable
 *  so callers can fall back. */
export function deriveBrandNameFromUrl(raw: string): string | null {
  const v = raw.trim();
  if (!v) return null;
  let host: string;
  try {
    host = new URL(/^https?:\/\//i.test(v) ? v : `https://${v}`).hostname;
  } catch {
    return null;
  }
  const labels = host
    .replace(/^www\./i, '')
    .split('.')
    .filter(Boolean);
  if (labels.length === 0) return null;
  // Second-to-last label is the registrable name for the common cases
  // (commitmind.dev → "commitmind", app.gestionalo.uy → "gestionalo").
  // Multi-part suffixes (.co.uk) aren't worth an eTLD table here — it's
  // a placeholder the founder edits.
  const main = labels.length >= 2 ? labels[labels.length - 2] : labels[0];
  return main && main.length > 0 ? main.toLowerCase() : null;
}
